import React from 'react';
import Field from '../Field';
import { Radio, Check, GroupLabel } from '../FormControls';
import { BALANCE_DATE_MODES, DEFAULT_ACCENT_COLOR, normaliseMessaging } from '../../domain/studio';
import {
  SETTINGS_WORDING as W, BALANCE_DATE_OPTIONS, negativeDisplayOptions, SEND_WINDOW_START_HOURS,
  accentColorDraft, isHexColor, balanceDayDraft, overdueDaysDraft, sendWindowEndHour, sendWindowLabel,
  sendWindowHelp, balanceDateNote, balanceSpecificDateHint,
} from '../../domain/studioSettings';
import { todayISO } from '../../domain/clock';
import { coreSync } from '../../lib/core';
import { formatTime, shortDatePattern, studioFormatters } from '../../domain/locale';
import { hasStoredTimeZone } from '../../lib/intlOptions';
import { useCommittedText, useCommittedValue, blurOnEnter } from './useCommitted';
import LogoField from './LogoField';
import SmsUnavailable from '../SmsUnavailable';
import { useSmsAvailable } from '../../context/SmsGateContext';
import { smsShown } from '../../domain/smsGate';
import s from './settings.module.css';
import { DateInput } from '../RegionInputs';

// Business Settings → Accounts & Invoices.
//
// ── What MMS has ───────────────────────────────────────────────────────────
// Five sections, in this order:
//
//   Family Account          Payment Methods, Quick Select, Default Balance Date
//   Invoice Settings        Automatic Late Payment Fee
//   Notifications & Reminders  SMS invoice notifications, overdue reminder,
//                           Email Timeframe
//   Invoice Formatting      Logo, Invoice Name, Invoice Number — the logo is
//                           the studio document's own field, not an
//                           `invoicing` leaf (#498); Invoice Number is #43's
//   Additional Settings     Negative Invoices, Show location on invoice, Group
//                           charges together by student, Include sales tax in
//                           line item amounts, Invoice Footer Text, Invoice
//                           Accent Color
//
// This app has no family payment processor, no late-fee machinery and no tax
// model, so Invoice Settings is absent rather than present and inert, and the
// other sections lack the rows that would be. Notifications & Reminders is
// complete: the reminder worker acts on the overdue reminder
// (functions/reminders/run.js), an invoice reminder due outside the Email
// Timeframe waits for it (#50, #97), and the SMS notification switch (#91) has
// a provider behind it (functions/delivery/transport/twilio.js).
//
// The section headings, the field labels and the option wording are MMS's,
// verbatim, including "Optional" as the hint on the two free-text fields.
//
// ── When it saves ──────────────────────────────────────────────────────────
// No Save button (the top of SettingsPage.jsx has why, a knowing divergence
// from MMS's [Discard] [Save Changes]). A radio, a checkbox or a select is a
// complete decision and writes on the spot; a typed field writes when it is
// left. Both rules are useCommitted.js. The write is one merging set naming
// this panel's own group (#337, data/useSettings.jsx), so nothing here can
// half-apply or carry another field over another device's edit.
//
// It is not mounted until the studio's record has been read: SettingsPage gates
// every panel on `useSettings().loading`, the only gate, so two answers to "may
// I be shown yet" cannot disagree. `saveStudio` holds a silent backstop and its
// reasoning.

// The two radios' options and the hex rule are `BALANCE_DATE_OPTIONS`,
// `NEGATIVE_DISPLAY_OPTIONS` and `accentColorDraft` in
// src/domain/studioSettings.js.

// The Email Timeframe's select.
//
// Whole hours only, because the worker wakes hourly (REMINDER_TIMING.runEvery).
//
// Through `formatTime` rather than a hard-coded "8:00 AM", so a studio whose
// Region writes 13:00 sees 13:00 — whether a locale writes AM/PM is a CLDR fact
// (locale.js).
//
// One option per hour, each naming the whole window as MMS does — "Between
// 8:00 AM and 9:00 AM": MMS has a single select with exactly these twenty-four
// options, from "Between 12:00 AM and 1:00 AM" to "Between 11:00 PM and 12:00
// AM", and no end control, so only the start is stored.
//
// The end wraps with `% 24`, so the last option closes at "12:00 AM" as MMS's
// does. It is a label, not a bound: the window is half-open and the worker
// compares the studio's hour against the start alone.
const windowLabel = (hour, locale) => sendWindowLabel(
  formatTime(`${hour}:00`, locale), formatTime(`${sendWindowEndHour(hour)}:00`, locale),
);

// `messaging` and `onChangeMessaging` are here because MMS puts the
// overdue-reminder controls on this tab, under Notifications & Reminders. The
// values are stored in the `messaging` group — the worker reads that group and
// nothing else (domain/reminders.js) — so the panel spans two groups and writes
// each through its own patch.
export default function InvoicingPanel({
  invoicing, onChange, messaging = {}, onChangeMessaging, locale, timeZone = null,
  logo = null, onChangeLogo = null,
}) {
  // Whether the studio has a zone of its own, asked through the function
  // StudioProfileCard's Time Zone row asks (#245), so the card and this warning
  // agree. The object literal is the cost of one predicate: the card holds the
  // whole studio record and this panel one field of it.
  const storedZone = hasStoredTimeZone({ timeZone });
  // Whether "Send SMS invoice notifications" can be taken (#782).
  const smsAvailable = useSmsAvailable();

  // Only what changed. The provider merges it into the stored group — passing
  // the whole object here would carry this render's stale copy of every other
  // field and undo a sibling change made a moment ago.
  const set = (patch) => onChange(patch);

  // Typed fields are held locally and committed when they are left; everything
  // else goes straight through. useCommitted.js holds that rule for Settings,
  // including following the record when it changes underneath, so an edit made
  // on another device lands here instead of being shadowed by a stale draft.
  //
  // normaliseInvoicing turns '' back into null, so an emptied field is "no
  // invoice name" rather than an empty-string name.
  const invoiceName = useCommittedText(
    invoicing.invoiceName, (v) => set({ invoiceName: v }),
  );
  const footerText = useCommittedText(
    invoicing.footerText, (v) => set({ footerText: v }),
  );

  // A colour the domain would reject is never written: `parse` hands back what
  // is stored, which is also what snaps the box back, so the swatch and the hex
  // box can never disagree with the invoice.
  const accentColor = useCommittedValue(
    invoicing.accentColor,
    (draft) => accentColorDraft(draft, invoicing.accentColor),
    (next) => set({ accentColor: next }),
  );

  // Held and committed on blur like the text fields, not written per
  // keystroke: typing "28" over "1" would write 2 and then 28, and clearing the
  // box to retype it would write the fallback 1.
  const balanceDayOfMonth = useCommittedValue(
    invoicing.balanceDayOfMonth,
    (draft) => balanceDayDraft(draft, invoicing),
    (next) => set({ balanceDayOfMonth: next }),
  );

  const overdueDays = useCommittedValue(
    messaging.invoiceOverdueDays,
    (draft) => overdueDaysDraft(draft, messaging),
    (invoiceOverdueDays) => onChangeMessaging({ invoiceOverdueDays }),
  );

  // The Email Timeframe, read through the normaliser rather than off the
  // record, so a document without the setting shows MMS's 8–9 default.
  const { sendWindowStartHour: startHour } = normaliseMessaging(messaging);

  return (
    <>
      <div className={s.panel}>
        <div className={s.panelTitle}>{W.familyAccount}</div>

        <div className={s.group}>
          <GroupLabel>{W.balanceDateLabel}</GroupLabel>
          {BALANCE_DATE_OPTIONS.map((o) => (
            <Radio
              key={o.id}
              name="balance-date-mode"
              checked={invoicing.balanceDateMode === o.id}
              onChange={() => set({ balanceDateMode: o.id })}
              label={o.label}
            />
          ))}

          {/* Only for the mode it belongs to — MMS reveals its day picker the
              same way rather than greying one out beside the other choices. */}
          {invoicing.balanceDateMode === BALANCE_DATE_MODES.dayOfMonth ? (
            <div className={s.indent}>
              <Field
                label={W.balanceDayLabel}
                htmlFor="balance-day-of-month"
                hint={W.balanceDayHint}
              >
                <input
                  id="balance-day-of-month"
                  className={s.dateInput}
                  type="number"
                  min={1}
                  max={28}
                  {...balanceDayOfMonth.field}
                  onKeyDown={blurOnEnter}
                />
              </Field>
            </div>
          ) : null}

          {invoicing.balanceDateMode === BALANCE_DATE_MODES.specificDate ? (
            <div className={s.indent}>
              <Field
                label={W.balanceDateField}
                htmlFor="balance-specific-date"
                // Null is a real state — the mode is chosen, the date is not
                // yet — and it has to say what happens meanwhile rather than
                // leave the fallback to be discovered on a report.
                hint={balanceSpecificDateHint(invoicing.balanceSpecificDate) ?? undefined}
              >
                <DateInput
                  id="balance-specific-date"
                  className={s.dateInput}
                  value={invoicing.balanceSpecificDate || ''}
                  // On change rather than on blur, unlike the typed fields
                  // above: a date input only fires change for a complete date,
                  // so there is no half-typed value to keep out of the record.
                  //
                  // A date input hands back a YYYY-MM-DD string, which is the
                  // stored shape exactly. Never parsed into a Date on the way
                  // through — see domain/studio.js on why.
                  onChange={(e) => set({ balanceSpecificDate: e.target.value })}
                />
              </Field>
            </div>
          ) : null}

          {/* What the setting resolves to right now. The same function the
              report uses, so the example cannot promise a date the report
              would not produce. */}
          <p className={s.note}>
            {balanceDateNote(coreSync().resolveBalanceDate(invoicing, todayISO()), shortDatePattern(locale))}
          </p>
        </div>
      </div>

      <div className={s.panel}>
        <div className={s.panelTitle}>Notifications &amp; Reminders</div>

        <div className={s.group}>
          {/* MMS's first row in this section, above the overdue reminder, in
              MMS's words (#91).

              The studio's answer to whether its families are texted about
              invoices at all, made once, here. A family's SMS-capable number is
              an address, not consent to use it for money (domain/reminders.js
              says the same at the invoice loop, which reads this).

              MMS's label names invoice notifications as well as reminders. This
              app sends no invoice notification texts, so the box governs the
              reminder; the label is MMS's because it is the same question.

              Independent of the checkbox below: ticking this one alone is a
              coherent state.

              Disabled and drawn off while texts cannot be sent (#782,
              domain/smsGate.js). A stored `true` is not rewritten, and the
              reminder worker queues a text the transport refuses. */}
          <Check
            checked={smsShown(messaging.sendInvoiceSms, smsAvailable)}
            // Guarded as well as disabled: a disabled box that still fired would
            // write the drawn `false` over a stored `true`.
            onChange={(e) => { if (smsAvailable) onChangeMessaging({ sendInvoiceSms: e.target.checked }); }}
            label={W.invoiceSmsCheck}
            help={W.invoiceSmsHelp}
            disabled={!smsAvailable}
          />
          {smsAvailable ? null : <SmsUnavailable />}

          {/* MMS's wording and shape: one checkbox, unticked on a new studio,
              which when ticked reveals a single number — one email, not a
              cadence. */}
          <Check
            checked={messaging.sendInvoiceReminders}
            onChange={(e) => onChangeMessaging({ sendInvoiceReminders: e.target.checked })}
            label={W.overdueCheck}
            help={W.overdueHelp}
          />
          {messaging.sendInvoiceReminders ? (
            <>
              <label className={s.groupHelp} htmlFor="invoice-overdue-days">
                {W.overdueDaysLabel}
              </label>
              <div className={s.numberRow}>
                <input
                  id="invoice-overdue-days"
                  type="number"
                  min="0"
                  max="90"
                  className={s.numberInput}
                  {...overdueDays.field}
                  onKeyDown={blurOnEnter}
                />
                <span className={s.unit}>{W.holdUnit}</span>
              </div>
            </>
          ) : null}
        </div>

        {/* MMS's Email Timeframe: one select of one-hour windows, its option
            wording and its 8–9 default, under Notifications & Reminders where
            MMS puts it. An overdue reminder that comes due outside the hour
            waits for it, so a family is not chased for money at eleven at
            night.

            The help text is MMS's sentence. It governs invoice reminders only
            (#97, domain/studio.js); a lesson reminder's time of day is the
            category's lead time, on Categories & Locations. */}
        <div className={s.group}>
          <GroupLabel>{W.emailTimeframe}</GroupLabel>
          <select
            aria-label={W.emailTimeframe}
            className={s.windowSelect}
            value={startHour}
            onChange={(e) => onChangeMessaging({ sendWindowStartHour: Number(e.target.value) })}
          >
            {SEND_WINDOW_START_HOURS.map((h) => (
              <option key={h} value={h}>{windowLabel(h, locale)}</option>
            ))}
          </select>
          <p className={s.groupHelp}>
            {sendWindowHelp(storedZone ? timeZone : null)}
            {/* Named, because these are the only hours on this screen that are
                not the reader's own clock. A studio with no zone stored has the
                worker fall back to UTC: `runReminders` reads
                `studio?.timeZone || fallbackZone` (functions/reminders/run.js),
                and `fallbackZone` is `REMINDER_DEFAULT_TIME_ZONE` or 'UTC'
                (functions/delivery/config.js). The app stores the device's zone
                on load (#620), so this is for a studio whose devices are on UTC
                or could not name one (`zone_to_record` in
                core/src/time_zone.rs). */}
          </p>
        </div>
      </div>

      <div className={s.panel}>
        <div className={s.panelTitle}>{W.invoiceFormatting}</div>

        {/* The logo heads Invoice Formatting in MMS, the same thumbnail and
            hint as Edit Studio Profile's. One value, shown and changed from
            both. */}
        {onChangeLogo ? (
          <div className={s.group}>
            <LogoField logo={logo} onChange={onChangeLogo} />
          </div>
        ) : null}

        <div className={s.group}>
          <Field
            label={W.invoiceName}
            htmlFor="invoice-name"
            hint={W.invoiceNameHint}
          >
            <input
              id="invoice-name"
              className={s.input}
              {...invoiceName.field}
              onKeyDown={blurOnEnter}
              placeholder={W.invoiceNamePlaceholder}
            />
          </Field>
        </div>
      </div>

      <div className={s.panel}>
        <div className={s.panelTitle}>{W.additionalSettings}</div>

        <div className={s.group}>
          <GroupLabel>{W.negativeInvoices}</GroupLabel>
          {negativeDisplayOptions(studioFormatters(locale).formatMoney(0)).map((o) => (
            <Radio
              key={o.id}
              name="negative-display"
              checked={invoicing.negativeDisplay === o.id}
              onChange={() => set({ negativeDisplay: o.id })}
              label={o.label}
            />
          ))}
        </div>

        <div className={s.group}>
          <Check
            checked={invoicing.showLocation}
            onChange={(e) => set({ showLocation: e.target.checked })}
            label={W.showLocation}
          />
          <Check
            checked={invoicing.groupChargesByStudent}
            onChange={(e) => set({ groupChargesByStudent: e.target.checked })}
            label={W.groupCharges}
          />
        </div>

        <div className={s.group}>
          <Field label={W.footerText} htmlFor="invoice-footer-text" hint={W.optional}>
            {/* No blurOnEnter: a footer is several lines and Enter is one. */}
            <textarea
              id="invoice-footer-text"
              className={s.textarea}
              rows={3}
              {...footerText.field}
            />
          </Field>
        </div>

        <div className={s.group}>
          <Field
            label={W.accentColor}
            htmlFor="invoice-accent-color"
            hint={W.accentColorHint}
          >
            <div className={s.colorRow}>
              {/* Two controls over one draft, so the well and the hex box
                  always read the same. The well cannot show a half-typed hex —
                  a type=color input with an invalid value silently renders
                  black — so it falls back to the default while the box is
                  mid-edit. */}
              <input
                id="invoice-accent-color"
                className={s.swatch}
                type="color"
                {...accentColor.field}
                value={isHexColor(accentColor.field.value)
                  ? accentColor.field.value
                  : DEFAULT_ACCENT_COLOR}
              />
              <input
                className={s.hexInput}
                aria-label="Invoice Accent Color (hex)"
                {...accentColor.field}
                onKeyDown={blurOnEnter}
                spellCheck={false}
              />
            </div>
          </Field>
        </div>
      </div>
    </>
  );
}
